import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { c, tracked, trackedBlock } from '../theme';
import { Notice, Wordmark } from '../components/Brand';
import { Menu } from '../components/Menu';
import { LoopingVideo } from '../components/LoopingVideo';
import { BRAND_GRADIENT, VioletCTA } from '../components/AuthScene';
import { ModulesBars, ProgressRing3D, SourcesRing } from '../components/Stat3D';
import { api, type ReportState, type ReportSummary, type User } from '../api';

const RING_CUES = ['CAN TAKE A FEW MINS', 'WORTH THE WAIT', 'ALMOST THERE'];

/** The overview card's frosted light scrim over the water. */
const CARD_SCRIM =
  'radial-gradient(70% 70% at 85% 10%, rgba(106,43,217,0.35) 0%, rgba(106,43,217,0) 70%), radial-gradient(60% 60% at 10% 90%, rgba(106,43,217,0.22) 0%, rgba(106,43,217,0) 70%), linear-gradient(180deg, rgba(245,238,219,0.55) 0%, rgba(245,238,219,0.62) 50%, rgba(245,238,219,0.78) 100%)';

const HOW_ITS_BUILT: { lead: string; rest: string; tone: string }[] = [
  {
    lead: 'Comparable sales.',
    rest: ' Recent settled sales of genuinely similar homes nearby, each adjusted for time, land and size. This carries the value.',
    tone: c.red,
  },
  {
    lead: 'An automated cross-check.',
    rest: ' An independent model on the same property — where one exists for it.',
    tone: c.violet,
  },
  {
    lead: 'Its own last sale,',
    rest: ' brought forward on the local index — a second opinion no model can give.',
    tone: c.cyan,
  },
];

export function Assembling({ user, reports }: { user: User | null; reports: ReportSummary[] }) {
  const { id = '' } = useParams();
  const [state, setState] = useState<ReportState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cue, setCue] = useState(0);
  // Bumped by "try again" to restart polling once a stopped report resumes.
  const [run, setRun] = useState(0);
  const [resuming, setResuming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    let timer: number;

    const poll = async () => {
      try {
        const next = await api.report(id);
        if (!live) return;
        setState(next);
        if (next.status === 'ready') {
          navigate(`/report/${id}`, { replace: true });
          return;
        }
        if (next.status === 'failed') return;
        timer = window.setTimeout(poll, 1500);
      } catch (err) {
        if (!live) return;
        setError(err instanceof Error ? err.message : 'Lost contact with the report.');
      }
    };

    void poll();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [id, navigate, run]);

  const resume = async () => {
    setResuming(true);
    setError(null);
    try {
      await api.resumeReport(id);
      setRun((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resume the report.');
    } finally {
      setResuming(false);
    }
  };

  useEffect(() => {
    const t = window.setInterval(() => setCue((n) => n + 1), 2600);
    return () => window.clearInterval(t);
  }, []);

  const percent = state?.percent ?? 0;
  const failed = state?.status === 'failed';
  const sections = state?.sectionsReady ?? 0;
  const sectionsTotal = state?.sectionsTotal ?? 19;
  const providers = state?.providersConfirmed ?? 0;
  const providersTotal = state?.providersTotal ?? 12;


  const assemblingOther = reports.find((r) => r.status === 'assembling' && r.id !== id) ?? null;

  return (
    <Stage>
      {/* 1 — Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '48px var(--gx) 0' }}>
        <button
          onClick={() => navigate('/search')}
          aria-label="Back to search"
          className="rb-circle"
          style={{
            width: 36,
            height: 36,
            margin: '-6px 0 -6px -8px',
            borderRadius: '50%',
            border: `1px solid ${c.ink18}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: c.ink,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M8.5 3L4 7l4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <Wordmark />
        <Menu
          current="assembling"
          user={user}
          reportCount={reports.length}
          assembling={
            state ? { id, address: state.address } : assemblingOther ? { id: assemblingOther.id, address: assemblingOther.address } : null
          }
        />
      </div>

      {/* 2 — The property, on the water card the overview uses */}
      <div
        style={{
          position: 'relative',
          margin: '28px 16px 0',
          padding: '22px 24px 20px',
          borderRadius: 28,
          overflow: 'hidden',
          isolation: 'isolate',
          transform: 'translateZ(0)',
          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
          textAlign: 'center',
        }}
      >
        <div aria-hidden style={{ position: 'absolute', inset: 0, transform: 'scale(2.25)', transformOrigin: 'top left' }}>
          <LoopingVideo src="/onboarding-bg.mp4" fallback={BRAND_GRADIENT} objectPosition="left top" />
        </div>
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: CARD_SCRIM, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }} />
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: failed ? c.red : c.violet, animation: failed ? 'none' : 'throb 2.4s ease-in-out infinite' }} />
            <span style={{ fontSize: 9, letterSpacing: '0.2em', fontWeight: 600, color: c.ink55 }}>{failed ? 'ASSEMBLY STOPPED' : 'BUILDING YOUR REPORT'}</span>
          </div>
          <div style={{ fontSize: (state?.line1 ?? '').length > 18 ? 26 : 30, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.03em', color: c.violet }}>{state?.line1 ?? '…'}</div>
          <div style={{ fontSize: 10, letterSpacing: '0.2em', color: c.ink, fontWeight: 700 }}>{(state?.locality ?? '').toUpperCase()}</div>
        </div>
      </div>

      {/* 3 — Progress: the 3D ring lighting up segment by segment */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '26px 0 0' }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 8, letterSpacing: '0.22em', fontWeight: 600, color: c.ink5 }}>REPORT PROGRESS</div>
          <div style={{ fontStretch: '125%', fontSize: 56, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 0.9 }}>
            {percent}
            <span style={{ fontSize: 20, fontWeight: 600, color: c.ink45, marginLeft: 2 }}>%</span>
          </div>
          <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 700, color: failed ? c.red : c.violet }}>
            {failed ? 'ASSEMBLY STOPPED' : RING_CUES[cue % RING_CUES.length]}
          </div>
        </div>
        <div style={{ marginTop: 6 }}>
          <ProgressRing3D percent={percent} size={290} />
        </div>
        {/* The real current stage */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0 20px 22px' }}>
            <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 16px 9px 12px',
              borderRadius: 999,
              background: 'rgba(106,43,217,0.1)',
              border: '1px solid rgba(106,43,217,0.3)',
              color: c.violet,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              textAlign: 'center',
              ...tracked(0.14),
            }}
          >
            <span style={{ width: 7, height: 7, flex: 'none', borderRadius: '50%', background: failed ? c.red : c.violet, animation: failed ? 'none' : 'throb 2.4s ease-in-out infinite' }} />
            {/* The real stage, not a rotating fiction. */}
            {failed ? 'Assembly stopped' : (state?.currentStage ?? 'Starting up')}
          </div>
        </div>
      </div>

      {/* 4 — Sections and providers, as the search page's 3D pieces filling in */}
      <Card shade={c.section1}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <Panel label="SECTIONS" done={sections} total={sectionsTotal} note="Each module is written once its evidence is in." side="left" viz={<ModulesBars n={sectionsTotal} lit={sections / Math.max(1, sectionsTotal)} />} />
          <Panel label="PROVIDERS" done={providers} total={providersTotal} note="Data sources confirmed so far." side="right" viz={<SourcesRing n={providersTotal} lit={providers / Math.max(1, providersTotal)} />} />
        </div>
      </Card>

      {/* 6 — While you wait */}
      <Card shade={c.section2} last>
        <div style={{ padding: '30px 16px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {(error || state?.error) && <Notice tone="error">{error ?? state?.error}</Notice>}
          {failed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 8px' }}>
              <VioletCTA onClick={resume} disabled={resuming}>
                {resuming ? 'resuming…' : 'try again'}
              </VioletCTA>
              <button
                type="button"
                onClick={() => navigate('/search')}
                style={{ alignSelf: 'center', fontSize: 10, letterSpacing: '0.14em', fontWeight: 600, color: c.red, textDecoration: 'underline' }}
              >
                START A DIFFERENT ADDRESS
              </button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 8px' }}>
            <div style={{ fontSize: 10, letterSpacing: '0.2em', color: c.red, fontWeight: 600 }}>WHILE YOU WAIT</div>
            <div style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.15 }}>
              <span style={{ fontWeight: 700 }}>How</span> the report is built<span style={{ color: c.red }}>.</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {HOW_ITS_BUILT.map((row, i) => (
              <div key={row.lead} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 14, alignItems: 'start', padding: '14px 14px', borderRadius: 20, background: c.tint }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 30%, #FFFFFF 0%, ${row.tone} 45%, ${row.tone} 100%)`,
                    boxShadow: `inset -2px -2px 3px rgba(0,0,0,0.22), 0 3px 6px ${alpha(row.tone, 0.35)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#FFFFFF',
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.55, color: c.ink75 }}>
                  <span style={{ color: c.ink, fontWeight: 600 }}>{row.lead}</span>
                  {row.rest}
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, lineHeight: 1.55, color: c.ink5, padding: '0 8px' }}>
            You get a range, not a single number — plus how closely those agree, and what widened it.
          </div>

          <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: c.ink45, ...trackedBlock(0.14) }}>
            {state ? `${state.sectionsReady} OF ${state.sectionsTotal} SECTIONS READY` : 'CONNECTING…'}
          </div>
        </div>
      </Card>
    </Stage>
  );
}

function Panel({
  label,
  done,
  total,
  note,
  viz,
  side,
}: {
  label: string;
  done: number;
  total: number;
  note: string;
  viz: React.ReactNode;
  side: 'left' | 'right';
}) {
  return (
    <div
      style={{
        padding: side === 'left' ? '26px 14px 24px 22px' : '26px 22px 24px 14px',
        borderRight: side === 'left' ? `1px solid ${c.hairline}` : undefined,
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ fontSize: 10, letterSpacing: '0.16em', color: c.red, fontWeight: 600 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 34, lineHeight: 1, fontWeight: 600 }}>{done}</span>
        <span style={{ fontSize: 14, color: c.ink5 }}>/ {total}</span>
      </div>
      {viz}
      <div style={{ fontSize: 11, lineHeight: 1.55, color: c.ink5 }}>{note}</div>
    </div>
  );
}

const alpha = (hex: string, a: number): string => {
  if (!hex.startsWith('#')) return `rgba(31,26,31,${a})`;
  const n = hex.slice(1);
  return `rgba(${parseInt(n.slice(0, 2), 16)},${parseInt(n.slice(2, 4), 16)},${parseInt(n.slice(4, 6), 16)},${a})`;
};

function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rb-stage"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: c.page,
        padding: '40px 0 60px',
      }}
    >
      <div
        className="rb-shell"
        style={{
          width: 390,
          maxWidth: '100%',
          background: c.screen,
          color: c.ink,
          borderRadius: 40,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** A section card in the overview's style: rounded, inset, its own grey. */
function Card({ shade, last, children }: { shade: string; last?: boolean; children: React.ReactNode }) {
  return <div style={{ margin: last ? '8px 8px 8px' : '8px 8px 0', borderRadius: 32, overflow: 'hidden', background: shade, ['--rb-surface' as string]: shade }}>{children}</div>;
}
